import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import { requireAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { useFunctions } from "hooks/useFunctions";
import FunctionChip from "components/Functions/FunctionChip";
import FunctionPicker from "components/Functions/FunctionPicker";
import ChannelTemplateEditor from "components/Notifications/ChannelTemplateEditor";
import {
  useNotificationEvents,
  useUpdateNotificationEvent,
} from "hooks/crm/useNotificationAdmin";
import { ArrowLeft, Mail, Send, Slack, Webhook, Bell, Check } from "lucide-react";

const ROUTING_CHANNELS = ["email", "telegram", "slack", "webhook", "inApp"];
const TEMPLATE_CHANNELS = ["telegram", "slack", "inApp"];
// defaultAudience is an ARRAY of these canonical values (backend validates
// Array.isArray against this set) — SOUND-523 fix.
const AUDIENCE_OPTIONS = ["internal", "client", "partner"];
const CHANNEL_META = {
  email: { label: "Email", icon: Mail },
  telegram: { label: "Telegram", icon: Send },
  slack: { label: "Slack", icon: Slack },
  webhook: { label: "Webhook", icon: Webhook },
  inApp: { label: "In-app", icon: Bell },
};

function Switch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-brand" : "bg-muted"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function NotificationEventEditor() {
  const { eventKey } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: events = [], isLoading } = useNotificationEvents();
  const updateEvent = useUpdateNotificationEvent();
  const { functions } = useFunctions({});

  const event = useMemo(() => events.find((e) => e.eventKey === eventKey) || null, [events, eventKey]);

  const [routing, setRouting] = useState(null);
  const [snapshot, setSnapshot] = useState("");
  const [tab, setTab] = useState("telegram");
  const seededRef = useRef(null);

  useEffect(() => {
    if (!event || seededRef.current === event.eventKey) return;
    const seed = {
      active: !!event.active,
      defaultAudience: Array.isArray(event.defaultAudience) ? event.defaultAudience : [],
      notifyFunctions: event.notifyFunctions || [],
      channels: Object.fromEntries(
        ROUTING_CHANNELS.map((c) => [c, { ...(event.channels?.[c] || {}) }])
      ),
    };
    setRouting(seed);
    setSnapshot(JSON.stringify(seed));
    seededRef.current = event.eventKey;
  }, [event]);

  const fnLabel = useMemo(() => {
    const m = {};
    functions.forEach((f) => (m[f.functionKey] = f.label));
    return (k) => m[k] || k;
  }, [functions]);

  if (isLoading || !routing) {
    return (
      <div>
        <PageHeader title={<Skeleton.Rect className="h-8 w-64" />} />
        <div className="container space-y-6 py-6">
          <Skeleton.Rect className="h-48 w-full" />
          <Skeleton.Rect className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-10 text-center">
        <p className="text-lg font-medium text-foreground">Event not found</p>
        <button className="btn btn-ghost mt-4" onClick={() => navigate("/settings/notification-config")}>
          <ArrowLeft className="h-4 w-4" />
          Back to catalog
        </button>
      </div>
    );
  }

  const setChannel = (ch, patch) =>
    setRouting((r) => ({ ...r, channels: { ...r.channels, [ch]: { ...r.channels[ch], ...patch } } }));
  const toggleAudience = (a) =>
    setRouting((r) => ({
      ...r,
      defaultAudience: r.defaultAudience.includes(a)
        ? r.defaultAudience.filter((x) => x !== a)
        : [...r.defaultAudience, a],
    }));
  const dirty = snapshot !== JSON.stringify(routing);

  const saveRouting = () => {
    updateEvent.mutate(
      {
        eventKey,
        updates: {
          active: routing.active,
          // Array of internal|client|partner — sent as-is (backend validates Array).
          defaultAudience: routing.defaultAudience,
          notifyFunctions: routing.notifyFunctions,
          channels: routing.channels,
        },
      },
      {
        onSuccess: () => {
          toast({ status: "success", title: "Routing saved" });
          setSnapshot(JSON.stringify(routing));
        },
        onError: (err) =>
          toast({
            status: "destructive",
            title: "Failed to save routing",
            description: err?.response?.data?.error || "Unknown error",
          }),
      }
    );
  };

  return (
    <div>
      <PageHeader
        sticky
        title={event.label || eventKey}
        badge={
          <span className={`badge ${routing.active ? "badge-success" : "badge-muted"}`}>
            {routing.active ? "Active" : "Inactive"}
          </span>
        }
        subtitle={
          <button
            onClick={() => navigate("/settings/notification-config")}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Notification config
          </button>
        }
        meta={[
          { label: "Key", value: eventKey },
          ...(event.domain ? [{ label: "Domain", value: event.domain }] : []),
        ]}
        actions={
          <button className="btn btn-primary" onClick={saveRouting} disabled={!dirty || updateEvent.isLoading}>
            {updateEvent.isLoading ? "Saving…" : "Save routing"}
          </button>
        }
      />

      <div className="container space-y-6 py-6">
        {/* Routing */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="mb-4 text-lg font-semibold">Routing</h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <span className="text-sm font-medium text-foreground">Active</span>
              <Switch
                checked={routing.active}
                onChange={(v) => setRouting((r) => ({ ...r, active: v }))}
                label="Event active"
              />
            </label>
            <div className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Default audience</span>
              <div className="flex flex-wrap items-center gap-1.5 py-1">
                {AUDIENCE_OPTIONS.map((a) => {
                  const on = (routing.defaultAudience || []).includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleAudience(a)}
                      aria-pressed={on}
                      className={`badge cursor-pointer capitalize ${on ? "badge-brand" : "badge-muted"}`}
                    >
                      {on ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <span className="h-3 w-3 rounded-[3px] border border-current opacity-50" />
                      )}
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Channels */}
          <div className="mt-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Channels
            </div>
            <div className="divide-y divide-border rounded-lg border border-border">
              {ROUTING_CHANNELS.map((ch) => {
                const meta = CHANNEL_META[ch];
                const Icon = meta.icon;
                const c = routing.channels[ch] || {};
                return (
                  <div key={ch} className="px-3 py-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-sm text-foreground">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {meta.label}
                        {ch === "email" && (
                          <span className="text-xs text-muted-foreground">· managed as EJS</span>
                        )}
                        {ch === "webhook" && c.webhookEvent && (
                          <span className="font-mono text-xs text-muted-foreground">· {c.webhookEvent}</span>
                        )}
                      </span>
                      <Switch
                        checked={!!c.enabled}
                        onChange={(v) => setChannel(ch, { enabled: v })}
                        label={`${meta.label} enabled`}
                      />
                    </div>
                    {ch === "slack" && c.enabled && (
                      <input
                        className="input mt-2 h-8 text-sm"
                        placeholder="Slack channel ID (e.g. C0123ABCD)"
                        value={c.channelId || ""}
                        onChange={(e) => setChannel("slack", { channelId: e.target.value })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notify functions */}
          <div className="mt-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Notify functions
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              Holders of these job functions are notified (in addition to the audience).
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {routing.notifyFunctions.map((k) => (
                <FunctionChip
                  key={k}
                  label={fnLabel(k)}
                  onRemove={() =>
                    setRouting((r) => ({
                      ...r,
                      notifyFunctions: r.notifyFunctions.filter((x) => x !== k),
                    }))
                  }
                />
              ))}
              <FunctionPicker
                functions={functions}
                assigned={routing.notifyFunctions}
                onPick={(k) =>
                  setRouting((r) => ({
                    ...r,
                    notifyFunctions: r.notifyFunctions.includes(k)
                      ? r.notifyFunctions
                      : [...r.notifyFunctions, k],
                  }))
                }
              />
            </div>
          </div>
        </section>

        {/* Template authoring */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="mb-1 text-lg font-semibold">Templates</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Author the message per channel. Email templates are managed as EJS (SOUND-519).
          </p>

          <div className="mb-4 flex flex-wrap gap-1 border-b border-border">
            {TEMPLATE_CHANNELS.map((ch) => {
              const meta = CHANNEL_META[ch];
              const Icon = meta.icon;
              const activeTab = tab === ch;
              return (
                <button
                  key={ch}
                  type="button"
                  onClick={() => setTab(ch)}
                  className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab
                      ? "border-brand text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {meta.label}
                </button>
              );
            })}
          </div>

          <ChannelTemplateEditor key={tab} eventKey={eventKey} channel={tab} />
        </section>
      </div>
    </div>
  );
}

export default requireAuth(NotificationEventEditor);
